<template>
  <div class="dashboard-wrapper">
    <h1 class="page-title">Dashboard</h1>

    <div id="main-scroll-area" class="page-content -form">
      <div class="thirds">
        <div class="input-field">
          Frequency:
          <select v-model="period" :disabled="status !== 'ready'">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <!--

        -->
        <div class="input-field">
          Type:
          <select v-model="reportType" :disabled="status !== 'ready'">
            <option value="conversations">Conversations</option>
            <option value="customers">Customers</option>
            <option value="messages">Messages Received</option>
            <option value="categories">Top Categories Queried</option>
          </select>
        </div>

        <div class="input-field">
          Dealership:
          <select v-model="dealership_id" :disabled="status !== 'ready'">
            <option value="">All</option>
            <option v-for="dealership in dealerships" :key="dealership.id" :value="dealership.id">
              {{ dealership.name }}
            </option>
          </select>
          <div v-if="dealerships.length === 0" class="error">
            There are no dealerships <router-link to="/dealerships/new">Create a Dealership</router-link>
          </div>
        </div>
      </div>

      <canvas id="chart-canvas" :class="{ '-loading': status === 'loading' }"></canvas>
      <div id="chart-tooltip"></div>

      <hr />

      <div class="halves">
        <div class="input-field">
          From:
          <VueDatePicker
            :model-value="statsFrom"
            @update:model-value="onDateFromChanged"
            enable-time-picker="false"
            format="yyyy-MM-dd"
            :max-date="statsTo"
          />
        </div>
        <div class="input-field">
          To:
          <VueDatePicker
            :model-value="statsTo"
            @update:model-value="onDateToChanged"
            enableTimePicker="false"
            format="yyyy-MM-dd"
            :min-date="statsFrom"
          />
        </div>
      </div>

      <hr />

      <a href="#" class="button -small" @click.prevent="fetchStats">Fetch Stats</a>

      <div v-if="statsStatus === 'loading'">Loading...</div>
      <div v-if="statsStatus === 'failed'">Failed to load stats. Please try again.</div>

      <div class="stats-container" :class="{ '-hidden': statsStatus !== 'ready' }">
        <ul>
          <li class="-title">Conversations</li>
          <li>Total: {{ stats.conversationCount }}</li>
          <li>Converted: {{ stats.convertedConversationCount }}</li>
          <li>Conversion Rate: {{ stats.convertedConversationPct }}%</li>
        </ul>

        <ul>
          <li class="-title">Customers</li>
          <li>Total: {{ stats.customerCount }}</li>
          <li>Converted: {{ stats.convertedCustomerCount }}</li>
          <li>Conversion Rate: {{ stats.convertedCustomerPct }}%</li>
        </ul>
      </div>
      <div>
        <MonthlyReport team="this.current"></MonthlyReport>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler, LineController
} from "chart.js";
import moment from "moment";
import { mapGetters, mapState } from "vuex";

// Register Chart.js plugins explicitly
Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController
);

import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import api from "@/helpers/api";
import auth from "@/helpers/auth";
import { colorArray } from "@/helpers/variables";

const apiVersion = 'v1';
const source = 'reports';

export default {

components: { VueDatePicker },

  beforeRouteEnter(to, from, next) {
    if (!auth.signedIn()) return next("/sign-in");
    next();
  },
  data() {
    return {
      period: "daily",
      reportType: "conversations",
      dealership_id: "",
      contextFilters: [],
      reports: [],
      status: "ready",
      tooltip: null,
      topCategories: [],
      retryCount: 0,

      statsStatus: "initalized",
      statsTo: new Date(),
      statsFrom: new Date(new Date().setDate(new Date().getDate() - 13)),
      statsFromDisabled: {},
      statsToDisabled: {},
      statsHighlighted: {},
      stats: {},
      reportData: [],
      initialized: false
    };
  },

  computed: {
    ...mapState({
      categories: (state) => state.categories.all,
      dealerships: (state) => state.dealerships.all
    }),
    ...mapGetters(["currentTeam"]),
    formattedStatsFrom() {
      if (!this.statsFrom) return null;
      const date = new Date(this.statsFrom);
      return date.toISOString().split('T')[0];
    },
    formattedStatsTo() {
      if (!this.statsTo) return null;
      const date = new Date(this.statsTo);
      return date.toISOString().split('T')[0];
    }
  },

     watch: {
       period(period) {
         this.updateStatsFrom(period);
         this.setStatsTo(new Date());
         this.updateReports();
       },

       reportType(type) {
         this.updateChart();
       },

       dealership_id(dealershipId) {
         this.updateReports();
       },

       currentTeam(newTeam) {
         if (newTeam && newTeam.settings && newTeam.settings.context_filters) {
           this.contextFilters = newTeam.settings.context_filters;
         }
         if (newTeam && newTeam.id) {
           this.updateReports();
         }
       },

       categories() {
         if (this.chartInstance && this.reportType === "categories") {
           this.updateChart();
         }
       },

       reportData() {
         if (this.chartInstance) {
           this.updateChart();
         }
       }
     },

   mounted() {
     // Store chart outside of Vue's reactivity system
     this.chartInstance = null;

     this.$nextTick(() => {
       this.chartInstance = this.initChart();
       this.tooltip = document.getElementById("chart-tooltip");

       if (this.currentTeam && this.currentTeam.id) {
         this.contextFilters = this.currentTeam.settings?.context_filters || [];
         this.updateReports();
       }

       this.initialized = true;
     });
   },
  methods: {
    initChart() {
       const canvas = document.getElementById("chart-canvas");
       if (!canvas) {
         return null;
       }

       const ctx = canvas.getContext("2d");
       if (!ctx) {
         return null;
       }

        const options = {
          responsive: true,
          maintainAspectRatio: true,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            title: {
              display: false
            },
            tooltip: {
              enabled: true,
              mode: 'index',
              intersect: false
            },
            legend: {
              display: true,
              position: 'top',
              fullSize: true
            },
            filler: {
              propagate: true
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                beginAtZero: true
              }
            },
            x: {
              display: true
            }
          }
        };

       return new Chart(ctx, {
         type: "line",
         data: { labels: [], datasets: [] },
         options
       });
     },

    updateStatsFrom(period) {
      let from = moment(this.statsTo).subtract(13, "days");
      if (period === "weekly") {
        from = moment(this.statsTo).subtract(11, "weeks");
      } else if (period === "monthly") {
        from = moment(this.statsTo).subtract(11, "months");
      }
      this.setStatsFrom(from.toDate());
    },

     updateReports() {
       this.reports = [];
       if (!this.currentTeam || !this.currentTeam.hashid) {
         this.status = "failed";
         return;
       }
       this.status = "loading";

       const params = {
         period: this.period,
         from: this.formattedStatsFrom,
         to: this.formattedStatsTo,
         team_id: this.currentTeam.hashid
       };
       if (this.dealership_id) {
         params.dealership_id = this.dealership_id;
       }
       api
         .instance()
         .get(`/${apiVersion}/${source}`, { params })
         .then(({ data }) => {
           this.status = "ready";

           // Handle multiple API response formats
           if (Array.isArray(data)) {
             this.reportData = data;
           } else if (data.data && Array.isArray(data.data)) {
             this.reportData = data.data.map((d) => d.attributes || d);
           } else {
             this.reportData = [];
           }

           this.retryCount = 0;
           this.updateChart();
         })
         .catch((err) => {
           this.status = "failed";
           this.retryCount = 0;
         });
     },
       updateChart() {
         if (!this.chartInstance) {
           return;
         }
         try {
           const labels = this.generateLabels();
           const datasets = this.generateDatasets();

           this.chartInstance.data.labels = labels;
           this.chartInstance.data.datasets = datasets;
           this.chartInstance.update();
         } catch (err) {
           console.error("Error updating chart:", err);
         }
       },

     generateLabels() {
       if (!this.reportData || this.reportData.length === 0) {
         return [];
       }
       return this.reportData.map((report) => {
         if (!report || !report.date) {
           return "Unknown";
         }
         try {
           if (this.period === "daily") {
             return moment(report.date).format("ddd Do");
           }

           if (this.period === "weekly") {
             let ends = moment(report.date).startOf("week").format("Do MMM");
             let starts = moment(report.date).startOf("week").subtract(6, "days").format("Do MMM");

             if (!report.id) {
               ends = moment(report.date).format("Do MMM");
               starts = moment(report.date).startOf("week").add(1, "day").format("Do MMM");
             }

             return `${starts} - ${ends}`;
           }

           return moment(report.date).format("MMM 'YY");
         } catch (err) {
           return "Invalid Date";
         }
       });
     },

      generateDatasets() {
        try {
          if (this.reportType === "categories") return this.generateCategoriesDataset();

          const datasets = [];

          if (this.reportType === "conversations") {
            datasets.push({ label: "Conversations", key: "convos" });
            datasets.push({ label: "Conversations Converted", key: "convo_leads" });
          }

          if (this.reportType === "customers") {
            datasets.push({ label: "Customers", key: "custs" });
            datasets.push({ label: "Customers Converted", key: "cust_leads" });
          }

          if (this.reportType === "messages") {
            datasets.push({ label: "Messages Received", key: "msgs" });
          }

          if (!Array.isArray(this.reportData)) {
            return [];
          }

          const result = datasets
            .map((dataset, index) => {
              const data = this.reportData.map((report) => {
                if (!report || !report.stats) {
                  return 0;
                }
                const value = report.stats[dataset.key] || 0;
                return value;
              });
              return this.buildDataset(dataset.label, data, index);
            })
            .filter((ds) => ds !== null);

          return result;
        } catch (err) {
          console.error("Error in generateDatasets:", err);
          return [];
        }
      },

      buildDataset(label, data, colorIndex) {
        if (!label || !Array.isArray(data)) {
          return null;
        }

        const safeIndex = (colorIndex >= 0 && colorIndex < colorArray.length) ? colorIndex : 0;
        const color = colorArray[safeIndex];

        return {
          label,
          data,
          borderColor: color,
          backgroundColor: color,
          fill: false,
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 5,
          pointHoverRadius: 8,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        };
      },

      generateCategoriesDataset() {
        const categories = this.generateTopCategories();
        if (!categories || categories.length === 0) {
          return [];
        }
        return categories
          .map((cat, index) => {
            if (!cat || !cat.label) return null;
            return this.buildDataset(cat.label, this.categoryDataFor(cat), index);
          })
          .filter((ds) => ds !== null);
      },

      generateTopCategories() {
        let cats = [];

         if (this.reportData && Array.isArray(this.reportData)) {
           this.reportData.forEach((row) => {
             if (!row || !row.stats || !row.stats.cats || !Array.isArray(row.stats.cats)) return;
             row.stats.cats.forEach((cat) => {
              if (!cat) return;
              const existing = cats.find((c) => c && c.id === cat.id);

              if (existing) {
                existing.count = (existing.count || 0) + (cat.count || 0);
              } else {
                cats.push(Object.assign({}, cat));
              }
            });
          });
        }

        cats = cats.sort((a, b) => {
          const aCount = (a && a.count) || 0;
          const bCount = (b && b.count) || 0;
          return aCount > bCount ? -1 : 1;
        });

        const categoriesArray = this.categories && Array.isArray(this.categories) ? this.categories : [];

        cats = cats.map((cat) => {
          if (!cat) return null;
          const fullCat = categoriesArray.find((c) => c && c.id === parseInt(cat.id));
          cat.label = (fullCat && fullCat.label)
            ? fullCat.label.replace(/_/g, " ")
            : "Unknown Category";
          // TODO: add an option on categories to "ignore from stats"
          if (cat.label && typeof cat.label === "string" && ["conversation end", "greeting", "fallback"].includes(cat.label)) {
            return null;
          }
          return cat;
        });

        this.topCategories = cats.filter(Boolean).slice(0, 10);

        return this.topCategories;
      },

      categoryDataFor(cat) {
        if (!cat || !this.reportData || !Array.isArray(this.reportData)) {
          return [];
        }

        const stats = this.reportData.map((data) => {
          if (!data || !data.stats || !data.stats.cats || !Array.isArray(data.stats.cats)) {
            return 0;
          }
          const stat = data.stats.cats.find((c) => c && c.id === cat.id);
          return stat ? stat.count : 0;
        });

        return stats;
      },

       renderTooltip({ title, body, opacity, caretX, caretY }) {
         if (!this.tooltip) {
           return;
         }

         // hide the tooltip when not hovering
         if (opacity === 0) {
           this.tooltip.style.display = "none";
           return;
         }

         if (!this.chartInstance || !this.chartInstance.data || !this.chartInstance.data.labels || !this.chartInstance.data.datasets) {
           return;
         }

         if (!title || !Array.isArray(title) || title.length === 0) {
           return;
         }

         const index = this.chartInstance.data.labels.indexOf(title[0]);
         if (index < 0) {
           return;
         }

         const values = this.chartInstance.data.datasets.map((d) => d.data[index]);
         const pct = values[0] && values[1] ? ((100 / values[0]) * values[1]).toFixed(1) : 0;

         let stats = [];

         if (this.reportType === "conversations") {
           stats.push({ label: "Conversations", value: values[0] });
           stats.push({ label: "Leads", value: values[1] });
           stats.push({ label: "Conversation Rate", value: `${pct || 0}%` });
         }

         if (this.reportType === "customers") {
           stats.push({ label: "Customers", value: values[0] });
           stats.push({ label: "Leads", value: values[1] });
           stats.push({ label: "Conversation Rate", value: `${pct || 0}%` });
         }

         if (this.reportType === "messages") {
           stats.push({ label: "Messages Received", value: values[0] });
         }

         if (this.reportType === "categories") {
           if (this.topCategories && Array.isArray(this.topCategories)) {
             stats = this.topCategories
               .filter((cat) => cat && cat.label && typeof cat.label === "string")
               .map((cat, i) => ({ label: cat.label, value: values[i] }))
               .sort((a, b) => (a.value > b.value ? -1 : 1));
           }
         }

         this.tooltip.innerHTML = `<span class="title">${title[0]}</span>`;

         stats.forEach((stat) => {
           this.tooltip.innerHTML += `<span class="stat"><span>${stat.label}</span><span>${stat.value}</span></span>`;
         });

         this.tooltip.style.display = "block";

         const position = this.chartInstance.canvas.getBoundingClientRect();

         let left = position.left - 180 + caretX;
         if (left + this.tooltip.offsetWidth + 200 > window.innerWidth) left -= 265;

         let top = position.top - 110 + caretY;
         if (top + this.tooltip.offsetHeight + 100 > window.innerHeight) top -= this.tooltip.offsetHeight - 60;

         const mainScrollArea = document.getElementById("main-scroll-area");
         if (mainScrollArea) {
           top += mainScrollArea.scrollTop;
         }

         this.tooltip.style.left = `${left}px`;
         this.tooltip.style.top = `${top}px`;
       },

     setStatsFrom(date) {
       this.statsFrom = date;

       this.statsToDisabled = { to: this.statsFrom };
       this.statsHighlighted = { from: this.statsFrom, to: this.statsTo };
     },

     setStatsTo(date) {
       this.statsTo = date;

       this.statsFromDisabled = { from: this.statsTo };
       this.statsHighlighted = { from: this.statsFrom, to: this.statsTo };
     },

     onDateFromChanged(newDate) {
       this.statsFrom = newDate;
       this.statsToDisabled = { to: this.statsFrom };
       this.statsHighlighted = { from: this.statsFrom, to: this.statsTo };
       this.updateReports();
     },

     onDateToChanged(newDate) {
       this.statsTo = newDate;
       this.statsFromDisabled = { from: this.statsTo };
       this.statsHighlighted = { from: this.statsFrom, to: this.statsTo };
       this.updateReports();
     },

     fetchStats() {
       if (this.statsStatus === "loading") return;

       this.statsStatus = "loading";

       const params = {
         period: "daily",
         from: this.formattedStatsFrom,
         to: this.formattedStatsTo,
         team_id: this.currentTeam.hashid
       };
       if (this.dealership_id) {
         params.dealership_id = this.dealership_id;
       }

       api
         .instance()
         .get(`/${apiVersion}/${source}`, { params })
         .then(({ data }) => {
           let conversationCount = 0;
           let convertedConversationCount = 0;
           let customerCount = 0;
           let convertedCustomerCount = 0;

           data.data.forEach((report) => {
             conversationCount += report.attributes.stats.convos;
             convertedConversationCount += report.attributes.stats.convo_leads;
             if (typeof report.attributes.stats.custs === "object") {
               customerCount += report.attributes.stats.custs.length;
             } else {
               customerCount += report.attributes.stats.custs;
             }
             if (typeof report.attributes.stats.cust_leads === "object") {
               convertedCustomerCount += report.attributes.stats.cust_leads.length;
             } else {
               convertedCustomerCount += report.attributes.stats.cust_leads;
             }
           });

           this.stats = {
             conversationCount,
             convertedConversationCount,
             convertedConversationPct: ((100 / conversationCount) * convertedConversationCount).toFixed(1),
             customerCount,
             convertedCustomerCount,
             convertedCustomerPct: ((100 / customerCount) * convertedCustomerCount).toFixed(1)
           };

           this.statsStatus = "ready";
         })
         .catch(() => {
           this.statsStatus = "failed";
         });
     }
  }
};
</script>
